/* ==============================================
   MARKDOWN — a renderer for the subset the pieces
   in src/content/articles are written in.

   That subset is: "## " headings, paragraphs,
   "- " lists, **bold** and *italic*. Nothing else
   appears in the corpus, so nothing else is
   handled — a markdown library would be several
   times the weight of the articles for features
   none of them use. If a piece ever needs a link,
   a table or a quote, add it here rather than
   reaching for a dependency.
   ============================================== */

/* Heading ids are derived from the text so the contents rail and the URL
   hash agree without the markdown carrying anchors. */
export const idOf = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/* **bold** and *italic*, left as plain text everywhere else. */
const inline = (text) => {
  const out = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let key = 0;
  let m = re.exec(text);

  while (m) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      m[1] !== undefined ? (
        <strong key={(key += 1)}>{m[1]}</strong>
      ) : (
        <em key={(key += 1)}>{m[2]}</em>
      )
    );
    last = re.lastIndex;
    m = re.exec(text);
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
};

/* A blank line ends a block. Within a paragraph, a single newline is a wrap
   in the source file rather than a break, so it collapses to a space. */
const blocks = (md) => md.trim().split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);

export const headingsOf = (md) =>
  blocks(md)
    .filter((b) => b.startsWith("## "))
    .map((b) => {
      const text = b.slice(3).trim();
      return { id: idOf(text), text };
    });

export const Markdown = ({ children }) => (
  <>
    {blocks(children).map((block, i) => {
      if (block.startsWith("## ")) {
        const text = block.slice(3).trim();
        return (
          <h2 key={i} id={idOf(text)}>
            {inline(text)}
          </h2>
        );
      }

      if (block.split("\n").every((l) => l.startsWith("- "))) {
        return (
          <ul key={i}>
            {block.split("\n").map((l, j) => (
              <li key={j}>{inline(l.slice(2).trim())}</li>
            ))}
          </ul>
        );
      }

      return <p key={i}>{inline(block.replace(/\s*\n\s*/g, " "))}</p>;
    })}
  </>
);

export default Markdown;
